import _ from 'lodash';
import cookie from 'react-cookies';

export class AuthManager {
  constructor(client) {
    this.client = client;
    this._storageProvider = this._createStorageProvider();
    this._authConfig = {
      containerKey: 'x-app-data',
      tokenKey: 'token'
    };
  }

  _safeJsonParse(text) {
    return _.attempt(JSON.parse, _.trim(text)) || {};
  }

  _toSemicolon(input) {
    const obj = _.isString(input) ? this._safeJsonParse(input) : input;
    return _(obj)
      .pickBy((v, k) => _.every([k, v], x => !_.isEmpty(_.toString(x))))
      .map((v, k) => `${k}=${v}`)
      .join(';');
  }

  _createStorageProvider() {
    const safeGet = (fn) => _.attempt(fn) || null;
    return {
      get: (key) => {
        const localValue = _.isObject(localStorage) ? safeGet(() => localStorage.getItem(key)) : null;
        return localValue || safeGet(() => cookie.load(key));
      }
    };
  }

  setStorageProvider(provider) {
    if (_.isObject(provider) && _.isFunction(provider.get)) {
      this._storageProvider = provider;
    }
    return this;
  }

  setAuthConfig(config) {
    this._authConfig = _.merge(this._authConfig, config);
    return this;
  }

  autoAuthSync(storageKey, callback) {
    const result = _.attempt(() => {
      const rawValue = this._storageProvider.get(storageKey);
      if (!rawValue) return null;
      
      const parsedValue = this._safeJsonParse(rawValue);
      const callbackResult = callback(parsedValue);
      
      // Extract token from callback result
      return _.isString(callbackResult) 
        ? callbackResult 
        : _.get(callbackResult, 'token');
    });
    
    if (!_.isError(result) && _.isString(result)) {
      this.setToken(result);
    }
    return this.client;
  }

  getCustomHeader() {
    if (!this.client.headerBasedEnabled) return {};
    
    const containerData = this._storageProvider.get(this._authConfig.containerKey);
    const headers = {};
    
    if (containerData) {
      headers[this._authConfig.containerKey] = this._toSemicolon(containerData);
    }
    
    // Always add Authorization header if token exists
    if (this.client.token && !_.isEmpty(this.client.token)) {
      headers.Authorization = `Bearer ${this.client.token}`;
    }
    
    return headers;
  }

  setToken(token) {
    if (_.isString(token) && !_.isEmpty(_.trim(token))) {
      this.client.token = _.trim(token);
      
      // Ensure axios headers are set
      const axios = _.get(this.client, 'axios');
      if (axios && _.has(axios, 'defaults.headers.common')) {
        _.set(axios, 'defaults.headers.common.Authorization', `Bearer ${this.client.token}`);
      }
    }
    return this;
  }

  clearToken() {
    this.client.token = null;
    _.unset(this.client, 'axios.defaults.headers.common.Authorization');
    return this;
  }

  isAuthenticated() {
    return !_.isEmpty(this.client.token);
  }

  resolveAuthHeader(existingHeaders = {}) {
    return _.merge(existingHeaders, this.getCustomHeader());
  }

  getAuthStatus() {
    return {
      isAuthenticated: this.isAuthenticated(),
      token: this.client.token ? `${this.client.token.substring(0, 10)}...` : null,
      headerBasedEnabled: this.client.headerBasedEnabled,
      currentHeaders: this.getCustomHeader(),
      axiosHeaders: _.get(this.client, 'axios.defaults.headers.common.Authorization')
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/lib/directus-sdk/auth.js